/**
 * Auto focus donation amount input when 'other' option is selected
 */
export const autoFocusDonationAmountInput = () => {
  // Wait till ENvalidate has finished setting up, otherwise event listeners may be removed

  window.onENValidateSetupCompleteArray = window.onENValidateSetupCompleteArray || [];
  window.onENValidateSetupCompleteArray.push(() => {
    // Listen at the document level, since EN's field update (e.g. on a currency change) can
    // replace the donation amount radio buttons, including the 'Other' one
    document.addEventListener('change', (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLInputElement) ||
        target.name !== 'transaction.donationAmt' ||
        target.value !== 'Other' ||
        !target.checked
      )
        return;

      // Slight delay for other input to be shown before trying to focus it
      setTimeout(
        () =>
          document
            .querySelector<HTMLInputElement>('[name="transaction.donationAmt.other"]')
            ?.focus(),
        500,
      );
    });
  });
};

/**
 * Select the 'Other' donation amount radio button when the 'other' amount text input receives
 * focus, so the two controls stay consistent regardless of which one the visitor interacts with
 * first.
 */
export const autoSelectOtherDonationAmountRadio = () => {
  const donationAmountOtherInput = document.querySelector(
    '[name="transaction.donationAmt.other"]',
  ) as HTMLInputElement | null;

  if (!donationAmountOtherInput) return;

  donationAmountOtherInput.addEventListener('focus', () => {
    const otherRadioButton = document.querySelector<HTMLInputElement>(
      '[name="transaction.donationAmt"][value="Other"]',
    );

    if (otherRadioButton) otherRadioButton.checked = true;
  });
};

/**
 * Show the donation frequency at the end of the submit button text (e.g. 'Donate £10 monthly' /
 * 'Donate £10 now'), or at the start where a language needs it (e.g. German), on
 * donation pages (URL contains '/donate' and a submit button is present), translated according to
 * the page locale. The frequency is set as a 'data-wp-frequency' (end) or 'data-wp-frequency-start'
 * attribute and displayed by CSS, leaving the button text itself to other scripts. Skipped if the
 * button text already starts or ends with a frequency word in any supported language. Updated
 * whenever the visitor switches between single and monthly donations (if the frequency radio
 * buttons are on the page).
 */
export const showDonationFrequencyOnSubmitButton = () => {
  if (!window.location.href.includes('/donate')) return;

  const submitButton = document.querySelector<HTMLButtonElement>('.en__submit button');
  if (!submitButton) return;

  // Each frequency is shown at the end of the button text unless its 'AtStart' flag is set
  const FREQUENCY_TEXT: Record<
    string,
    { monthly: string; monthlyAtStart?: boolean; single: string; singleAtStart?: boolean }
  > = {
    de: { monthly: 'Monatlich', monthlyAtStart: true, single: 'Jetzt', singleAtStart: true },
    en: { monthly: 'monthly', single: 'now' },
    es: { monthly: 'al mes', single: 'ahora' },
  };

  // Matches button text already starting or ending with a frequency word in any supported language
  const frequencyWords = Object.values(FREQUENCY_TEXT).flatMap(({ monthly, single }) => [
    monthly,
    single,
  ]);
  const endsWithFrequencyRegex = new RegExp(`(?:^|\\s)(?:${frequencyWords.join('|')})$`, 'i');
  const startsWithFrequencyRegex = new RegExp(`^(?:${frequencyWords.join('|')})(?:\\s|$)`, 'i');

  // Language code only, e.g. 'en' from 'en-GB'; falls back to English for unsupported languages
  const language = (window.pageJson?.locale || 'en').split(/[-_]/)[0].toLowerCase();
  const frequencyText = FREQUENCY_TEXT[language] || FREQUENCY_TEXT.en;

  // Wait till ENvalidate has finished setting up, so the frequency field values are available
  window.onENValidateSetupCompleteArray = window.onENValidateSetupCompleteArray || [];
  window.onENValidateSetupCompleteArray.push(() => {
    // The frequency is shown by CSS (::before / ::after content from the
    // 'data-wp-frequency-start' / 'data-wp-frequency' attributes) rather than written into the
    // button text, so it can't conflict with other scripts that rewrite the text (e.g. to update
    // the currency symbol and amount)
    const updateButtonFrequency = () => {
      submitButton.removeAttribute('data-wp-frequency');
      submitButton.removeAttribute('data-wp-frequency-start');

      // Leave button text that already starts or ends with a frequency alone (the pseudo-elements
      // aren't part of textContent, so this only sees the real text)
      const buttonText = (submitButton.textContent || '').trim();
      if (startsWithFrequencyRegex.test(buttonText) || endsWithFrequencyRegex.test(buttonText))
        return;

      // Use the frequency radio buttons if on this page, otherwise the value stored from an
      // earlier page
      const checkedRadioButton = document.querySelector<HTMLInputElement>(
        'input[name="transaction.recurrpay"]:checked',
      );
      const recurrpay =
        checkedRadioButton?.value ?? window.getStoredFieldValue('transaction.recurrpay');

      const isMonthly = recurrpay === 'Y';
      const atStart = isMonthly ? frequencyText.monthlyAtStart : frequencyText.singleAtStart;

      submitButton.setAttribute(
        atStart ? 'data-wp-frequency-start' : 'data-wp-frequency',
        isMonthly ? frequencyText.monthly : frequencyText.single,
      );
    };

    updateButtonFrequency();

    // Other scripts may rewrite the button text to end with a frequency word themselves.
    // Attribute changes aren't observed, so setting our attribute can't retrigger this.
    new MutationObserver(updateButtonFrequency).observe(submitButton, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    // Listen at the document level, since EN's field update can replace the frequency fields
    document.addEventListener('change', (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.name === 'transaction.recurrpay')
        updateButtonFrequency();
    });
  });
};

/**
 * Add a 'data-wp-error' attribute to '.en__field--donationAmt' whenever it contains an
 * '.en__field__error' element, and remove the attribute again once that error message is gone.
 * The error message can be added/removed multiple times as the visitor corrects their input, so
 * this uses a MutationObserver rather than a one-off check.
 */
export const syncDonationAmountErrorState = () => {
  const donationAmountField = document.querySelector(
    '.en__field--donationAmt',
  ) as HTMLElement | null;

  if (!donationAmountField) return;

  const updateErrorState = () => {
    const hasError = !!donationAmountField.querySelector('.en__field__error');

    if (hasError) donationAmountField.setAttribute('data-wp-error', 'true');
    else donationAmountField.removeAttribute('data-wp-error');
  };

  updateErrorState();

  new MutationObserver(updateErrorState).observe(donationAmountField, {
    childList: true,
    subtree: true,
  });
};

/**
 * Keep the '--other-amount' custom property (used by the donation amount 'other' item's ::after
 * backdrop, in place of the input's own value, since the page blanks that input whenever the
 * 'Other' radio isn't selected) in sync with the current donation amount: reflects the selected
 * radio button's value on load and on every subsequent change (skipping the 'Other' radio itself,
 * since its value isn't an amount), and the 'other' input's own value as the visitor types into
 * it. The backdrop is hidden while the input has focus via the ':focus-within' rule in
 * _forms.scss, so also restore the current amount into the input itself on focus (it otherwise
 * shows blank, having just been hidden behind the backdrop).
 */
export const syncDonationAmountOtherBackdrop = () => {
  // Wait till ENvalidate has finished setting up, otherwise event listeners may be removed

  window.onENValidateSetupCompleteArray = window.onENValidateSetupCompleteArray || [];
  window.onENValidateSetupCompleteArray.push(() => {
    // EN's field update (e.g. on a currency change) can replace the donation amount radio
    // buttons, so look elements up fresh each time and listen at the document level rather
    // than attaching listeners to the elements themselves
    const getOtherItem = () =>
      document.querySelector<HTMLElement>('.en__field--donationAmt .en__field__item--other');

    let currentAmount = '';

    const setBackdropAmount = (amount: string) => {
      currentAmount = amount;
      getOtherItem()?.style.setProperty('--other-amount', JSON.stringify(amount));
    };

    const setBackdropAmountFromSelectedRadio = () => {
      const selectedRadioButton = document.querySelector<HTMLInputElement>(
        '[name="transaction.donationAmt"]:checked',
      );

      if (selectedRadioButton && selectedRadioButton.value !== 'Other')
        setBackdropAmount(selectedRadioButton.value);
    };

    setBackdropAmountFromSelectedRadio();

    document.addEventListener('change', (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.name === 'transaction.donationAmt')
        setBackdropAmountFromSelectedRadio();
    });

    // EN's field update swaps in new amount buttons after a currency change, possibly after a
    // delay, so re-sync whenever the buttons change rather than guessing when it has finished
    const donationAmountField = document.querySelector('.en__field--donationAmt');

    if (donationAmountField)
      new MutationObserver(setBackdropAmountFromSelectedRadio).observe(donationAmountField, {
        childList: true,
        subtree: true,
      });

    document.addEventListener('input', (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.name === 'transaction.donationAmt.other')
        setBackdropAmount(target.value);
    });

    // 'focus' doesn't bubble, so use 'focusin' to catch it at the document level
    document.addEventListener('focusin', (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.name === 'transaction.donationAmt.other')
        target.value = currentAmount;
    });
  });
};

/**
 * Styles passed into VGS card fields shown inside iframes.
 *
 * https://knowledge.engagingnetworks.net/youraccount/enhancing-security-at-engaging-networks#EnhancingSecurityatEngagingNetworks-HowtomatchyourVGSfieldstylewithyourtextfields
 */
export const vgsCardFieldStyles = () => {
  const borderColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-light-grey')
    .trim();
  const borderFocusColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-off-black')
    .trim();
  const border = `2px solid ${borderColour}`;
  const borderRadius = '4px';
  const boxSizing = 'border-box';
  const errorColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-error')
    .trim();
  const focusBorderWidth = '4px';
  const focusBoxShadow = 'none';
  const focusOutline = `2px solid ${borderFocusColour}`;
  const focusOutlineOffset = '0';
  const fontSize = '16px';
  const height = '44px';
  const padding = '8px';
  const placeholderColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-mid-grey')
    .trim();
  const textColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-off-black')
    .trim();

  const css = {
    border,
    'border-radius': borderRadius,
    'box-sizing': boxSizing,
    color: textColour,
    'font-size': fontSize,
    height,
    padding,
    '&:focus-visible': {
      'border-color': borderColour,
      'box-shadow': focusBoxShadow,
      outline: focusOutline,
      'border-width': focusBorderWidth,
      'outline-offset': focusOutlineOffset,
    },
    '&::placeholder': {
      color: placeholderColour,
    },
    '&.invalid.touched': {
      color: errorColour,
    },
    '&.valid': {
      color: textColour,
    },
    '@font-face': {
      'font-family': 'ASTTCommons',
      'font-style': 'normal',
      'font-weight': '400',
      src: "url(https://we-could-even.github.io/we-planet-engaging-networks/fonts/ASTTCommons/ASTTCommonsRegular.woff2) format('woff2')",
    },
    'font-family':
      '"ASTTCommons", blinkmacsystemfont, -apple-system, "Segoe UI", roboto, oxygen, ubuntu, cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", helvetica, arial, sans-serif',
  };

  window.enVGSFields = {
    'transaction.ccnumber': {
      css,
    },
    'transaction.ccvv': {
      css,
    },
  };
};
